/**
 * Storage durability (risk R4, NFR-REL-1).
 *
 * The outbox of unpushed events is the only data in the system that exists in
 * exactly one place. Everything else can be re-fetched from the server. If the
 * browser evicts IndexedDB under storage pressure, unpushed work is gone and no
 * recovery is possible after the fact -- so the only real mitigations are to ask
 * for persistent storage up front, push eagerly rather than efficiently, and tell
 * the user plainly when persistence was refused.
 */
export type StorageDurability = 'persisted' | 'best-effort' | 'unavailable';

export async function requestPersistentStorage(): Promise<StorageDurability> {
  if (!('storage' in navigator) || typeof navigator.storage.persist !== 'function') {
    return 'unavailable';
  }
  try {
    if (await navigator.storage.persisted()) return 'persisted';
    return (await navigator.storage.persist()) ? 'persisted' : 'best-effort';
  } catch {
    return 'unavailable';
  }
}

export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (!('storage' in navigator) || typeof navigator.storage.estimate !== 'function') return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usage, quota };
  } catch {
    return null;
  }
}
